<template>
  <div class="flex flex-col mt-2">
    <div class="w-60 h-px bg-nera-border mb-2"></div>
    <div class="flex items-center gap-2">
      <span class="text-xs text-nera-muted">{{ questionText }}</span>
      <div class="flex gap-2">
        <button @click="submitFeedback('positive')"
          :class="[feedbackBtn, feedback === 'positive' ? activeBtn : idleBtn]" :disabled="submitting"
          aria-label="Mark as helpful">
          <i class="fas fa-thumbs-up" :class="{ 'animate-feedback-pop': feedback === 'positive' }"></i>
        </button>
        <button @click="submitFeedback('negative')"
          :class="[feedbackBtn, feedback === 'negative' ? activeBtn : idleBtn]" :disabled="submitting"
          aria-label="Mark as not helpful">
          <i class="fas fa-thumbs-down" :class="{ 'animate-feedback-pop': feedback === 'negative' }"></i>
        </button>
        <button :class="[feedbackBtn, hasReport ? activeBtn : idleBtn]" :disabled="submitting" @click="openReportModal"
          aria-label="Report an issue" title="Report Issue">
          <i class="fas fa-flag" :class="{ 'animate-feedback-pop': hasReport }"></i>
        </button>
      </div>

      <Transition enter-active-class="transition-opacity duration-300" enter-from-class="opacity-0"
        leave-active-class="transition-opacity duration-300" leave-to-class="opacity-0">
        <div v-if="showReportModal" @click.self="closeModal"
          class="fixed inset-0 z-9999 flex items-center justify-center p-6 bg-black/60 backdrop-blur-md">
          <div
            class="w-full max-w-110 bg-nera-elevated-6 border border-nera-light-border rounded-[1.25rem] shadow-[0_20px_50px_rgba(0,0,0,0.25)] overflow-hidden animate-modal-up">
            <header class="flex items-center justify-between px-6 py-5">
              <div class="flex items-center gap-3 text-nera-text-light">
                <i class="fas fa-circle-exclamation text-nera-text-light text-[1.1rem]"></i>
                <h3>Report an issue</h3>
              </div>
              <button @click="closeModal"
                class="w-7 h-7 inline-flex items-center justify-center rounded-full bg-nera-light-hover text-nera-muted-light cursor-pointer transition-all duration-200 hover:bg-red-100 hover:text-red-600">×</button>
            </header>

            <div class="px-6 pb-6">
              <p class="mb-4 text-[0.85rem] text-nera-muted-light">
                Your feedback helps us improve response quality.
              </p>
              <textarea v-model="reportText"
                placeholder="What's wrong with this response? (e.g., incorrect, offensive, slow...)" rows="5"
                class="w-full p-4 box-border resize-none text-[0.95rem] leading-normal rounded-xl text-nera-text-light bg-nera-elevated-3 border border-nera-light-border transition-all duration-200 focus:outline-none focus:border-nera-light-border-strong focus:bg-nera-elevated-1 focus:shadow-[0_0_0_1px_rgba(0,0,0,0.05)]" />
            </div>

            <footer class="flex justify-end gap-3 px-6 py-5 bg-nera-light-hover">
              <button @click="closeModal"
                class="px-5 py-2.5 rounded-xl text-sm font-medium cursor-pointer border-0 bg-transparent text-nera-muted-light transition-all duration-200 hover:text-nera-text-light">
                Dismiss
              </button>
              <button @click="submitReport" :disabled="!reportText.trim() || submitting"
                class="px-5 py-2.5 rounded-xl text-sm font-medium cursor-pointer border-0 bg-nera-accent text-nera-text-muted-light shadow-[0_4px_12px_rgba(51,117,214,0.3)] transition-all duration-200 enabled:hover:bg-nera-accent-hover enabled:hover:-translate-y-px disabled:opacity-40 disabled:cursor-not-allowed">
                {{ submitting ? "Sending..." : "Send Report" }}
              </button>
            </footer>
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";

const props = defineProps({
  messageId: {
    type: [String, Number],
    required: true,
  },
  questionText: {
    type: String,
    default: "Was this helpful?",
  },
  existingFeedback: {
    type: String,
    default: null,
  },
  existingReport: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(["feedback-submitted"]);
const feedback = ref(props.existingFeedback);
const submitting = ref(false);
const showReportModal = ref(false);
const reportText = ref("");
const hasReport = ref(!!props.existingReport);

const feedbackBtn = "p-1 text-sm cursor-pointer transition-all duration-150 bg-transparent border-0 disabled:cursor-not-allowed enabled:hover:scale-[1.2] enabled:hover:opacity-100";
const idleBtn = "opacity-60 text-nera-muted";
const activeBtn = "opacity-100 text-nera-accent";

const openReportModal = () => {
  showReportModal.value = true;
};

const closeModal = () => {
  showReportModal.value = false;
  reportText.value = "";
};

const postFeedback = async (payload) => {
  const csrfToken = document.querySelector('[name="csrf-token"]')?.content;
  const response = await fetch(`/api/messages/${props.messageId}/feedback`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken,
      Accept: "application/json",
    },
    credentials: "same-origin",
    body: JSON.stringify(payload),
  });

  if (!response.ok) throw new Error("Failed to submit feedback");
  return response.json();
};

const submitFeedback = async (type, report = null) => {
  if (submitting.value || (feedback.value === type && !report)) return;

  const previousFeedback = feedback.value;
  feedback.value = type;
  submitting.value = true;

  try {
    await postFeedback({ feedback: { feedback: type, report } });
    emit("feedback-submitted", {
      messageId: props.messageId,
      feedback: type,
      report,
    });
  } catch (error) {
    console.error("Failed to submit feedback:", error);
    feedback.value = previousFeedback;
  } finally {
    submitting.value = false;
  }
};

const submitReport = async () => {
  if (submitting.value || !reportText.value.trim()) return;
  await submitFeedback(
    feedback.value || "negative",
    reportText.value.trim(),
  );
  hasReport.value = true;
  closeModal();
};
</script>
